import { useQuery } from '@tanstack/react-query'
import { getMe } from '../services/auth-api'

export function useMe() {
  return useQuery({
    queryKey: ['auth-me'],
    queryFn: getMe,
    retry: (failureCount, error) => {
      // Don't retry if account is blocked or session expired — it won't change
      if (isAccountBlocked(error)) return false
      if (isSessionExpired(error)) return false
      return failureCount < 3
    },
  })
}

/**
 * Check if an API error is an account_blocked response (403).
 *
 * Supports two backend response formats:
 * - New: { status: 403, data: { detail: "...", code: "account_blocked" } }
 * - Legacy: { status: 403, data: { detail: "...deactivated/deleted..." } }
 */
export function isAccountBlocked(error: unknown): boolean {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error) ||
    !('data' in error)
  ) {
    return false
  }

  const { status, data } = error as { status: number; data: Record<string, unknown> }
  if (status !== 403 || typeof data !== 'object' || data === null) return false

  // Explicit code field (preferred)
  if (data.code === 'account_blocked') return true

  // Fallback: match on detail message keywords
  const detail = data.detail
  if (typeof detail === 'string') {
    const lower = detail.toLowerCase()
    return (
      lower.includes('deactivated') ||
      lower.includes('has been deleted') ||
      lower.includes('no longer active') ||
      lower.includes('organisation') ||
      lower.includes('organization')
    )
  }

  return false
}

/**
 * Check if an API error is a session expired / revoked response (401).
 */
export function isSessionExpired(error: unknown): boolean {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error)
  ) {
    return false
  }
  return (error as { status: number }).status === 401
}

/**
 * Extract a human-readable message from an API error.
 * Falls back to a generic message if nothing useful is found.
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data: Record<string, unknown> }).data
    if (typeof data.detail === 'string') return data.detail
  }
  if (error instanceof Error) return error.message
  return 'Something went wrong while loading your account details. Please try again or contact support.'
}

/**
 * Extract the blocked message from an account_blocked error.
 */
export function getBlockedMessage(error: unknown): string {
  if (isAccountBlocked(error)) {
    const data = (error as { data: Record<string, unknown> }).data
    const detail = data.detail
    if (typeof detail === 'string') return detail
  }
  return 'Your account is no longer active. Please contact your company\'s admin/support.'
}
